/**
 * GraphQL Collection Mutation Hooks
 *
 * Hooks for collection operations: create, update, delete, manage photos.
 */

import { useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// GraphQL Mutations
const CREATE_COLLECTION_MUTATION = gql`
  mutation CreateCollection($input: CreateCollectionInput!) {
    createCollection(input: $input) {
      id
      globalId
      name
      userId
    }
  }
`;

const UPDATE_COLLECTION_MUTATION = gql`
  mutation UpdateCollection($input: UpdateCollectionInput!) {
    updateCollection(input: $input) {
      id
      name
      description
      heroPhotoId
    }
  }
`;

const DELETE_COLLECTION_MUTATION = gql`
  mutation DeleteCollection($input: DeleteCollectionInput!) {
    deleteCollection(input: $input) {
      success
      message
    }
  }
`;

const ADD_PHOTO_TO_COLLECTION_MUTATION = gql`
  mutation AddPhotoToCollection($input: AddPhotoToCollectionInput!) {
    addPhotoToCollection(input: $input) {
      success
      message
    }
  }
`;

const UPDATE_COLLECTION_ITEM_MUTATION = gql`
  mutation UpdateCollectionItem($input: UpdateCollectionItemInput!) {
    updateCollectionItem(input: $input) {
      success
      message
    }
  }
`;

const REMOVE_PHOTO_FROM_COLLECTION_MUTATION = gql`
  mutation RemovePhotoFromCollection($input: RemovePhotoFromCollectionInput!) {
    removePhotoFromCollection(input: $input) {
      success
      message
    }
  }
`;

const REORDER_COLLECTION_PHOTOS_MUTATION = gql`
  mutation ReorderCollectionPhotos($input: ReorderCollectionPhotosInput!) {
    reorderCollectionPhotos(input: $input) {
      success
      message
    }
  }
`;

const CREATE_CRITIQUE_SESSION_MUTATION = gql`
  mutation CreateCritiqueSession($input: CreateCritiqueSessionInput!) {
    createCritiqueSession(input: $input)
  }
`;

/**
 * Resume AI critique session mutation
 * Feature 071 Phase 10: Paused state for AI sessions
 */
const RESUME_AI_CRITIQUE_SESSION_MUTATION = gql`
  mutation ResumeAICritiqueSession($input: ResumeAICritiqueSessionInput!) {
    resumeAICritiqueSession(input: $input)
  }
`;

/**
 * Update collection hero style mutation
 * Feature 653: Collection Hero Image Settings
 */
const UPDATE_COLLECTION_HERO_STYLE_MUTATION = gql`
  mutation UpdateCollectionHeroStyle($input: UpdateCollectionHeroStyleInput!) {
    updateCollectionHeroStyle(input: $input) {
      id
      globalId
      name
      heroStyle
    }
  }
`;

// Types
interface CollectionPayload {
  id: string;
  globalId: string;
  name: string;
  userId: string;
}

interface UpdateCollectionPayload {
  id: string;
  name: string;
  description?: string;
}

interface SuccessPayload {
  success: boolean;
  message?: string;
}

interface CritiqueSessionPayload {
  id: string;
  globalId: string;
  title: string;
  slug: string;
  state: string;
  createdAt: string;
}

// Input types
interface CreateCollectionInput {
  name: string;
  description?: string;
}

interface UpdateCollectionInput {
  id: string;
  name?: string;
  description?: string;
  showExif?: boolean;
  textPosition?: string;
  heroPhotoId?: string;
}

interface DeleteCollectionInput {
  id: string;
}

interface AddPhotoToCollectionInput {
  collectionId: string;
  photoId: string;
  displayOrder?: number;
}

interface UpdateCollectionItemInput {
  collectionId: string;
  photoId: string;
  textContent?: string;
  textPosition?: string;
}

interface RemovePhotoFromCollectionInput {
  collectionId: string;
  photoId: string;
}

interface ReorderCollectionPhotosInput {
  collectionId: string;
  photoIds: string[];
}

/**
 * N Comments configuration
 * Feature: 651-n-comments-task
 */
interface NCommentsConfigInput {
  targetCount: number;
}

interface CreateCritiqueSessionInput {
  collectionId: string;
  title: string;
  description?: string;
  intent?: 'open' | 'ai_only';
  allowedAiPersonas?: string[];
  feedbackOptions?: string[];  // Feature 649: enabled feedback types ('general', 'rating', 'n_comments')
  nCommentsConfig?: NCommentsConfigInput;  // Feature 651: N Comments configuration
}

/**
 * Input for resuming a paused AI critique session
 * Feature 071 Phase 10
 */
interface ResumeAICritiqueSessionInput {
  sessionId: string;
  description?: string;
}

/**
 * Hero style type
 * Feature 653: Collection Hero Image Settings
 */
export type HeroStyle = 'collage' | 'first_image';

/**
 * Input for updating collection hero style
 * Feature 653: Collection Hero Image Settings
 */
interface UpdateCollectionHeroStyleInput {
  collectionId: string;
  heroStyle: HeroStyle;
}

/**
 * Payload for updating collection hero style
 * Feature 653: Collection Hero Image Settings
 */
interface UpdateCollectionHeroStylePayload {
  id: string;
  globalId: string;
  name: string;
  heroStyle: HeroStyle;
}


/**
 * Response payload for resumed AI critique session
 * Feature 071 Phase 10
 */
interface ResumeAICritiqueSessionPayload {
  id: string;
  globalId: string;
  title: string;
  slug: string;
  description?: string;
  intent: string;
  isPaused: boolean;
  aiJobsQueued: number;
}


/**
 * Hook for creating a collection
 */
export function useCreateCollectionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { createCollection: CollectionPayload },
    { input: CreateCollectionInput }
  >(CREATE_COLLECTION_MUTATION);

  const createCollection = async (input: CreateCollectionInput) => {
    logger.info('Collections', 'Creating collection via GraphQL', { name: input.name });

    const result = await mutate({ variables: { input } });

    if (result.data?.createCollection) {
      logger.info('Collections', 'Collection created', { id: result.data.createCollection.id });
    }

    return result.data?.createCollection;
  };

  return {
    createCollection,
    data: data?.createCollection,
    loading,
    error,
  };
}

/**
 * Hook for updating a collection
 */
export function useUpdateCollectionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { updateCollection: UpdateCollectionPayload },
    { input: UpdateCollectionInput }
  >(UPDATE_COLLECTION_MUTATION);

  const updateCollection = async (input: UpdateCollectionInput) => {
    logger.info('Collections', 'Updating collection via GraphQL', { id: input.id });

    const result = await mutate({ variables: { input } });

    return result.data?.updateCollection;
  };

  return {
    updateCollection,
    data: data?.updateCollection,
    loading,
    error,
  };
}

/**
 * Hook for deleting a collection
 */
export function useDeleteCollectionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { deleteCollection: SuccessPayload },
    { input: DeleteCollectionInput }
  >(DELETE_COLLECTION_MUTATION);

  const deleteCollection = async (id: string) => {
    logger.info('Collections', 'Deleting collection via GraphQL', { id });

    const result = await mutate({ variables: { input: { id } } });

    return result.data?.deleteCollection;
  };

  return {
    deleteCollection,
    data: data?.deleteCollection,
    loading,
    error,
  };
}

/**
 * Hook for adding a photo to a collection
 */
export function useAddPhotoToCollectionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { addPhotoToCollection: SuccessPayload },
    { input: AddPhotoToCollectionInput }
  >(ADD_PHOTO_TO_COLLECTION_MUTATION);

  const addPhotoToCollection = async (input: AddPhotoToCollectionInput) => {
    logger.info('Collections', 'Adding photo to collection via GraphQL', {
      collectionId: input.collectionId,
      photoId: input.photoId,
    });

    const result = await mutate({
      variables: { input },
      refetchQueries: ['GetCollectionPhotos', 'GetCollection', 'GetPhotos'],
    });

    return result.data?.addPhotoToCollection;
  };

  return {
    addPhotoToCollection,
    data: data?.addPhotoToCollection,
    loading,
    error,
  };
}

/**
 * Hook for updating a collection item
 */
export function useUpdateCollectionItemGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { updateCollectionItem: SuccessPayload },
    { input: UpdateCollectionItemInput }
  >(UPDATE_COLLECTION_ITEM_MUTATION);

  const updateCollectionItem = async (input: UpdateCollectionItemInput) => {
    logger.info('Collections', 'Updating collection item via GraphQL', {
      collectionId: input.collectionId,
      photoId: input.photoId,
    });

    const result = await mutate({ variables: { input } });

    return result.data?.updateCollectionItem;
  };

  return {
    updateCollectionItem,
    data: data?.updateCollectionItem,
    loading,
    error,
  };
}

/**
 * Hook for removing a photo from a collection
 */
export function useRemovePhotoFromCollectionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { removePhotoFromCollection: SuccessPayload },
    { input: RemovePhotoFromCollectionInput }
  >(REMOVE_PHOTO_FROM_COLLECTION_MUTATION);

  const removePhotoFromCollection = async (input: RemovePhotoFromCollectionInput) => {
    logger.info('Collections', 'Removing photo from collection via GraphQL', {
      collectionId: input.collectionId,
      photoId: input.photoId,
    });

    const result = await mutate({ variables: { input } });

    return result.data?.removePhotoFromCollection;
  };

  return {
    removePhotoFromCollection,
    data: data?.removePhotoFromCollection,
    loading,
    error,
  };
}

/**
 * Hook for reordering collection photos
 */
export function useReorderCollectionPhotosGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { reorderCollectionPhotos: SuccessPayload },
    { input: ReorderCollectionPhotosInput }
  >(REORDER_COLLECTION_PHOTOS_MUTATION);

  const reorderCollectionPhotos = async (input: ReorderCollectionPhotosInput) => {
    logger.info('Collections', 'Reordering collection photos via GraphQL', {
      collectionId: input.collectionId,
      count: input.photoIds.length,
    });

    const result = await mutate({
      variables: { input },
      // Invalidate collections list to update hero images
      refetchQueries: ['GetCollections'],
    });

    return result.data?.reorderCollectionPhotos;
  };

  return {
    reorderCollectionPhotos,
    data: data?.reorderCollectionPhotos,
    loading,
    error,
  };
}

/**
 * Hook for creating a critique session
 */
export function useCreateCritiqueSessionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { createCritiqueSession: CritiqueSessionPayload },
    { input: CreateCritiqueSessionInput }
  >(CREATE_CRITIQUE_SESSION_MUTATION);

  const createCritiqueSession = async (input: CreateCritiqueSessionInput) => {
    logger.info('Collections', 'Creating critique session via GraphQL', {
      collectionId: input.collectionId,
      title: input.title,
      intent: input.intent,
      allowedAiPersonasCount: input.allowedAiPersonas?.length,
      feedbackOptions: input.feedbackOptions,
      nCommentsConfig: input.nCommentsConfig,  // Feature 651
    });

    const result = await mutate({ variables: { input } });

    if (result.data?.createCritiqueSession) {
      logger.info('Collections', 'Critique session created', {
        id: result.data.createCritiqueSession.id,
      });
    }

    return result.data?.createCritiqueSession;
  };

  return {
    createCritiqueSession,
    data: data?.createCritiqueSession,
    loading,
    error,
  };
}

/**
 * Hook for resuming a paused AI critique session
 * Feature 071 Phase 10: Paused state for AI sessions
 */
export function useResumeAICritiqueSessionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { resumeAICritiqueSession: ResumeAICritiqueSessionPayload },
    { input: ResumeAICritiqueSessionInput }
  >(RESUME_AI_CRITIQUE_SESSION_MUTATION);

  const resumeAICritiqueSession = async (input: ResumeAICritiqueSessionInput) => {
    logger.info('Collections', 'Resuming AI critique session via GraphQL', {
      sessionId: input.sessionId,
      hasDescription: !!input.description,
    });

    const result = await mutate({
      variables: { input },
      // Refetch critique sessions to update status
      refetchQueries: ['GetCritiqueSessions', 'GetCritiqueSession'],
    });

    if (result.data?.resumeAICritiqueSession) {
      logger.info('Collections', 'AI critique session resumed', {
        id: result.data.resumeAICritiqueSession.id,
        aiJobsQueued: result.data.resumeAICritiqueSession.aiJobsQueued,
      });
    }

    return result.data?.resumeAICritiqueSession;
  };

  return {
    resumeAICritiqueSession,
    data: data?.resumeAICritiqueSession,
    loading,
    error,
  };
}

/**
 * Hook for updating a collection's hero style
 * Feature 653: Collection Hero Image Settings
 */
export function useUpdateCollectionHeroStyleGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { updateCollectionHeroStyle: UpdateCollectionHeroStylePayload },
    { input: UpdateCollectionHeroStyleInput }
  >(UPDATE_COLLECTION_HERO_STYLE_MUTATION);

  const updateCollectionHeroStyle = async (input: UpdateCollectionHeroStyleInput) => {
    logger.info('Collections', 'Updating collection hero style via GraphQL', {
      collectionId: input.collectionId,
      heroStyle: input.heroStyle,
    });

    const result = await mutate({
      variables: { input },
      // Refetch collections to update hero images (both list and detail views)
      refetchQueries: ['GetCollections', 'GetCollection'],
    });

    if (result.data?.updateCollectionHeroStyle) {
      logger.info('Collections', 'Collection hero style updated', {
        id: result.data.updateCollectionHeroStyle.id,
        heroStyle: result.data.updateCollectionHeroStyle.heroStyle,
      });
    }

    return result.data?.updateCollectionHeroStyle;
  };

  return {
    updateCollectionHeroStyle,
    data: data?.updateCollectionHeroStyle,
    loading,
    error,
  };
}

/**
 * Combined hook for all collection mutations
 */
export function useCollectionMutationsGraphQL() {
  return {
    useCreateCollection: useCreateCollectionGraphQL,
    useUpdateCollection: useUpdateCollectionGraphQL,
    useDeleteCollection: useDeleteCollectionGraphQL,
    useAddPhotoToCollection: useAddPhotoToCollectionGraphQL,
    useUpdateCollectionItem: useUpdateCollectionItemGraphQL,
    useRemovePhotoFromCollection: useRemovePhotoFromCollectionGraphQL,
    useReorderCollectionPhotos: useReorderCollectionPhotosGraphQL,
    useCreateCritiqueSession: useCreateCritiqueSessionGraphQL,
    useResumeAICritiqueSession: useResumeAICritiqueSessionGraphQL,
    useUpdateCollectionHeroStyle: useUpdateCollectionHeroStyleGraphQL, // Feature 653
  };
}
